import { initialize } from "launchdarkly-js-client-sdk";
import { kAllAudiences, type Audience } from "@/client/core/Core.api";
import {
    parseForcedAudiences,
    resolveAudiences,
    type AudienceState,
} from "@/client/core/services/audience";
import { authTokenSignal } from "@/client/core/services/auth-token.signal";
import { getWebAuthConfig } from "@/client/core/services/web-auth.service";
import { decodeJwtPayload } from "@/client/utils/decodeJwtPayload";
import { createSignalWithValue } from "@/client/utils/signal";
import { setTelemetryAudience } from "@/client/utils/telemetry";

// Splice's shared LaunchDarkly project. Client-side IDs are public
// identifiers (staging: 5bb63da15c271236a7e1375a via VITE_LD_CLIENT_SIDE_ID).
const kProductionClientSideId = "5baa838ab6db572117ce5260";

// Set on every access token by the Auth0 post-login Action "Add User ID to
// Access Token" — the bare Splice user UUID, matching how every other Splice
// surface keys LaunchDarkly contexts (and how segments target users).
const kUserUuidClaim = "https://splice.com/user_uuid";

const kLdInitTimeoutSeconds = 5;
const kDevTokenWaitMs = 1500;
const kEmbeddedTokenWaitMs = 8000;
const kForceAudienceStorageKey = "labs-force-audience";

export const audienceStateSignal = createSignalWithValue<AudienceState>({
    isReady: false,
    audiences: new Set(),
    resolvedAudiences: new Set(),
    flags: {},
});

let didInit = false;

const warn = (message: string, detail?: unknown): void => {
    // eslint-disable-next-line no-console
    console.warn(`[labs] feature-flags: ${message}`, detail ?? "");
};

const getClientSideId = (): string => {
    const override = import.meta.env.VITE_LD_CLIENT_SIDE_ID;
    return typeof override === "string" && override !== "" ? override : kProductionClientSideId;
};

/**
 * `?forceAudience=` is persisted in sessionStorage because in-app navigation
 * uses full page loads that drop query params (same pattern as the sidepanel
 * host flag). `?forceAudience=clear` removes it.
 */
const syncForcedAudienceFromUrl = (): void => {
    const parsed = parseForcedAudiences(window.location.search);
    if (parsed === undefined) {
        return;
    }
    try {
        if (parsed === "clear") {
            window.sessionStorage.removeItem(kForceAudienceStorageKey);
        } else {
            const raw = new URLSearchParams(window.location.search).get("forceAudience");
            if (raw !== null) {
                window.sessionStorage.setItem(kForceAudienceStorageKey, raw);
            }
        }
    } catch {
        // Storage unavailable — the override lasts only this page.
    }
};

const readStoredForcedAudience = (): string | null => {
    try {
        return window.sessionStorage.getItem(kForceAudienceStorageKey);
    } catch {
        return null;
    }
};

const getStoredForcedAudiences = (): ReadonlySet<Audience> | undefined => {
    const raw = readStoredForcedAudience();
    if (raw === null) {
        return undefined;
    }
    const parsed = parseForcedAudiences(`forceAudience=${raw}`);
    if (parsed === "none") {
        return new Set();
    }
    return parsed === undefined || parsed === "clear" ? undefined : parsed;
};

let lastResolved: ReadonlySet<Audience> | undefined;
let lastFlags: Record<string, boolean> = {};

const publish = (resolved: ReadonlySet<Audience>, flags: Record<string, boolean>): void => {
    lastResolved = resolved;
    lastFlags = flags;
    const forced = getStoredForcedAudiences();
    // The preview override is available in dev builds and to R&D users
    // (menu toggle / ?forceAudience=). Gating is UX-only, so an abused
    // override exposes nothing the API doesn't already allow.
    const canForce = import.meta.env.DEV || resolved.has("rnd");
    const isForced = forced !== undefined && canForce;
    const audiences = isForced ? forced : resolved;
    setTelemetryAudience(audiences.size > 0 ? [...audiences].sort().join(",") : undefined);
    audienceStateSignal.notify({
        isReady: true,
        audiences,
        resolvedAudiences: resolved,
        forcedAudience: isForced ? (readStoredForcedAudience() ?? undefined) : undefined,
        flags,
    });
};

/**
 * Programmatic preview switch (the menu's "view as" toggle). Persists like
 * `?forceAudience=` and re-publishes immediately; `undefined` resets to the
 * real resolved audiences.
 */
export const setForcedAudience = (raw: string | undefined): void => {
    try {
        if (raw === undefined) {
            window.sessionStorage.removeItem(kForceAudienceStorageKey);
        } else {
            window.sessionStorage.setItem(kForceAudienceStorageKey, raw);
        }
    } catch {
        // Storage unavailable — the override lasts only this page.
    }
    if (lastResolved !== undefined) {
        publish(lastResolved, lastFlags);
    }
};

const publishFallback = (): void => {
    // Dev ergonomics: `npm run start` with no auth/LD configured shows
    // everything, matching pre-gating behavior. Production fails restrictive.
    publish(import.meta.env.DEV ? new Set(kAllAudiences) : new Set(), {});
};

const startLdClient = async (accessToken: string): Promise<void> => {
    const userUuid = decodeJwtPayload(accessToken)?.[kUserUuidClaim];
    if (typeof userUuid !== "string" || userUuid === "") {
        warn(`token is missing the ${kUserUuidClaim} claim`);
        publishFallback();
        return;
    }
    try {
        const client = initialize(getClientSideId(), {
            kind: "user",
            key: userUuid,
            client: "labs-live",
        });
        await client.waitForInitialization(kLdInitTimeoutSeconds);
        const readFlags = (): void => {
            const all = client.allFlags();
            const flags: Record<string, boolean> = {};
            for (const [key, value] of Object.entries(all)) {
                if (typeof value === "boolean") {
                    flags[key] = value;
                }
            }
            publish(resolveAudiences(flags), flags);
        };
        readFlags();
        client.on("change", readFlags);
    } catch (error) {
        warn("initialization failed — falling back", error);
        publishFallback();
    }
};

/**
 * Boots LaunchDarkly once the first auth token arrives and publishes the
 * user's audience tiers to `audienceStateSignal`. Idempotent; called from
 * both document boot paths (createCore and the home page).
 */
export const initFeatureFlags = (): void => {
    if (didInit || typeof window === "undefined") {
        return;
    }
    didInit = true;
    syncForcedAudienceFromUrl();

    let fallbackTimer: ReturnType<typeof setTimeout> | undefined;
    const isEmbedded = window.parent !== window;
    if (import.meta.env.DEV) {
        if (!isEmbedded && getWebAuthConfig() === undefined) {
            // Not embedded and the auth gate is dormant: no token source
            // exists at all, so don't make local dev wait for one.
            publishFallback();
        } else {
            fallbackTimer = setTimeout(() => {
                publishFallback();
            }, kDevTokenWaitMs);
        }
    } else if (isEmbedded) {
        // Embedded hosts without the auth bridge (older plugin builds) never
        // deliver a token; resolve restrictive instead of leaving the page
        // waiting forever.
        fallbackTimer = setTimeout(() => {
            publishFallback();
        }, kEmbeddedTokenWaitMs);
    }

    let didStartLd = false;
    authTokenSignal.listenAndInit((state) => {
        if (didStartLd || state.accessToken === undefined) {
            return;
        }
        didStartLd = true;
        if (fallbackTimer !== undefined) {
            clearTimeout(fallbackTimer);
        }
        void startLdClient(state.accessToken);
    });
};
